import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, X, ChevronsUpDown, ChevronsDownUp } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import { FaqItem } from "@/components/faq/FaqItem";
import { FaqSidebar, FaqChips } from "@/components/faq/FaqNav";
import { FAQ_ICONS } from "@/components/faq/faqIcons";
import { stripLinks } from "@/components/faq/FaqRichText";
import { FAQ_CATEGORIES } from "@/data/faq";

const URL = "https://garcin.cl/faq";
const MODIFIED = "2026-10-07T00:00:00-03:00";
const TOTAL = FAQ_CATEGORIES.reduce((n, c) => n + c.items.length, 0);

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const SEARCH_INDEX = FAQ_CATEGORIES.flatMap((c) =>
  c.items.map((i) => ({ id: i.id, cat: c.id, text: norm(`${i.q} ${stripLinks(i.a.join(" "))}`) }))
);

const RELATED = [
  { to: "/que-es-eos", label: "¿Qué es EOS?" },
  { to: "/el-modelo-eos", label: "El Modelo EOS" },
  { to: "/implementacion", label: "Cómo se implementa EOS" },
  { to: "/implementador-eos", label: "Implementador EOS" },
  { to: "/dolores-del-empresario", label: "Los dolores del empresario" },
  { to: "/rompiendo-el-techo", label: "Blog: Rompiendo el techo" },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${URL}#faqpage`,
  url: URL,
  name: "Preguntas frecuentes sobre EOS®",
  inLanguage: "es-CL",
  datePublished: MODIFIED,
  dateModified: MODIFIED,
  author: { "@id": "https://garcin.cl/#carlos-garcin" },
  about: { "@type": "Thing", name: "EOS — Entrepreneurial Operating System" },
  mainEntity: FAQ_CATEGORIES.flatMap((c) =>
    c.items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: stripLinks(i.a.join(" ")) },
    }))
  ),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Inicio", item: "https://garcin.cl/" },
    { "@type": "ListItem", position: 2, name: "Preguntas frecuentes", item: URL },
  ],
};

export default function PreguntasFrecuentes() {
  useReveal();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState({});
  const [activeId, setActiveId] = useState(FAQ_CATEGORIES[0].id);

  useEffect(() => {
    setPageSEO({
      title: "Preguntas frecuentes sobre EOS® | Carlos Garcín",
      description:
        "Respuestas claras a las preguntas más comunes sobre EOS: qué es, sus herramientas, la implementación, las reuniones, las Rocas y el rol del Implementador.",
      path: "/faq",
      keywords:
        "preguntas frecuentes EOS, qué es EOS, EOS Entrepreneurial Operating System, implementador EOS, V/TO, Rocas EOS, Reunión de Nivel 10, IDS EOS, Accountability Chart, GWC, Carlos Garcín",
    });
    window.scrollTo(0, 0);
  }, []);

  const matches = useMemo(() => {
    const q = norm(query.trim());
    return new Set(SEARCH_INDEX.filter((i) => !q || i.text.includes(q)).map((i) => i.id));
  }, [query]);

  const counts = useMemo(() => {
    const c = {};
    FAQ_CATEGORIES.forEach((cat) => {
      c[cat.id] = cat.items.filter((i) => matches.has(i.id)).length;
    });
    return c;
  }, [matches]);

  const searching = query.trim().length > 0;
  const visibleIds = FAQ_CATEGORIES.flatMap((c) => c.items).filter((i) => matches.has(i.id)).map((i) => i.id);
  const allOpen = visibleIds.length > 0 && visibleIds.every((id) => searching || open[id]);

  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (!hash) return;
    const t = setTimeout(() => {
      if (SEARCH_INDEX.some((i) => i.id === hash)) setOpen((o) => ({ ...o, [hash]: true }));
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(e.target.dataset.cat)),
      { rootMargin: "-20% 0px -70% 0px" }
    );
    FAQ_CATEGORIES.forEach((c) => {
      const el = document.getElementById(`cat-${c.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const onToggle = useCallback(
    (id, isOpen) => {
      if (!searching) setOpen((o) => (o[id] === isOpen ? o : { ...o, [id]: isOpen }));
    },
    [searching]
  );

  const onJump = (e, id) => {
    e.preventDefault();
    document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#cat-${id}`);
    setActiveId(id);
  };

  const toggleAll = () => {
    const next = !allOpen;
    setOpen((o) => ({ ...o, ...Object.fromEntries(visibleIds.map((id) => [id, next])) }));
  };

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="Preguntas frecuentes"
          title={
            <>
              Preguntas frecuentes sobre <span className="text-[#F26522]">EOS®</span>
            </>
          }
          subtitle="Respuestas a las preguntas más comunes sobre EOS, sus herramientas, el proceso de implementación y cómo puede ayudar a tu empresa a funcionar mejor."
        />

        <section className="bg-white pt-10 sm:pt-12 pb-16 sm:pb-20" data-testid="faq-content">
          <div className="container-eos max-w-6xl">
            <div className="lg:grid lg:grid-cols-[230px_minmax(0,1fr)] lg:gap-14 lg:items-start">
              <FaqSidebar categories={FAQ_CATEGORIES} counts={counts} activeId={activeId} onJump={onJump} />

              <div className="min-w-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Busca una pregunta: Rocas, IDS, Integrador…"
                      aria-label="Buscar en las preguntas frecuentes"
                      data-testid="faq-search-input"
                      className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-10 text-sm sm:text-base text-navy-800 placeholder:text-slate-400 focus:border-[#F26522] focus:outline-none focus:ring-2 focus:ring-[#F26522]/20 [&::-webkit-search-cancel-button]:hidden"
                    />
                    {query && (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        aria-label="Limpiar búsqueda"
                        data-testid="faq-search-clear"
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:text-[#F26522]"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={toggleAll}
                    disabled={searching || visibleIds.length === 0}
                    data-testid="faq-toggle-all"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-navy-800 transition-colors hover:border-[#F26522] hover:text-[#F26522] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-navy-800"
                  >
                    {allOpen ? <ChevronsDownUp className="h-4 w-4" /> : <ChevronsUpDown className="h-4 w-4" />}
                    {allOpen ? "Contraer todo" : "Expandir todo"}
                  </button>
                </div>

                <p aria-live="polite" data-testid="faq-result-count" className="mt-3 text-sm text-slate-500">
                  {searching
                    ? `${visibleIds.length} ${visibleIds.length === 1 ? "resultado" : "resultados"} para “${query.trim()}”`
                    : `${TOTAL} preguntas en ${FAQ_CATEGORIES.length} temas`}
                </p>

                <FaqChips categories={FAQ_CATEGORIES} counts={counts} activeId={activeId} onJump={onJump} />

                {searching && visibleIds.length === 0 && (
                  <div data-testid="faq-no-results" className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
                    <p className="font-display font-bold text-navy-800 text-lg">No encontramos preguntas con ese término.</p>
                    <p className="mt-2 text-slate-500 text-sm">Prueba con otra palabra o escríbeme directamente tu duda.</p>
                    <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                      <button type="button" onClick={() => setQuery("")} className="text-sm font-bold text-navy-800 hover:text-[#F26522]">
                        Ver todas las preguntas
                      </button>
                      <Link to="/contacto" className="text-sm font-bold text-[#F26522] hover:underline">
                        Escríbeme tu duda →
                      </Link>
                    </div>
                  </div>
                )}

                {FAQ_CATEGORIES.map((c, idx) => {
                  const Icon = FAQ_ICONS[c.icon];
                  return (
                    <section
                      key={c.id}
                      id={`cat-${c.id}`}
                      data-cat={c.id}
                      aria-labelledby={`cat-title-${c.id}`}
                      data-testid={`faq-category-${c.id}`}
                      className={`scroll-mt-32 lg:scroll-mt-28 ${idx === 0 ? "mt-8" : "mt-12"} ${counts[c.id] === 0 ? "hidden" : ""}`}
                    >
                      <div className="flex items-center gap-4 border-b-2 border-navy-800 pb-4">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F26522]/10">
                          <Icon className="h-5 w-5 text-[#F26522]" strokeWidth={1.7} />
                        </div>
                        <h2 id={`cat-title-${c.id}`} className="flex-1 font-display font-extrabold text-navy-800 text-xl sm:text-2xl leading-tight">
                          {c.title}
                        </h2>
                        <span className="shrink-0 text-xs font-bold tabular-nums text-slate-400">
                          {counts[c.id]} {counts[c.id] === 1 ? "pregunta" : "preguntas"}
                        </span>
                      </div>
                      <div>
                        {c.items.map((item) => (
                          <FaqItem
                            key={item.id}
                            item={item}
                            hidden={!matches.has(item.id)}
                            isOpen={searching ? true : !!open[item.id]}
                            onToggle={onToggle}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-12 sm:py-14 border-y border-slate-200" data-testid="faq-related">
          <div className="container-eos max-w-6xl reveal">
            <p className="text-xs font-bold tracking-[0.22em] uppercase text-[#F26522]">Sigue explorando</p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {RELATED.map((r) => (
                <li key={r.to}>
                  <Link
                    to={r.to}
                    data-testid={`faq-related-${r.to.slice(1)}`}
                    className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-4 font-display font-bold text-navy-800 transition-colors hover:border-[#F26522] hover:text-[#F26522]"
                  >
                    {r.label}
                    <ArrowRight className="h-4 w-4 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-[#F26522]" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section data-testid="faq-final-cta" className="relative overflow-hidden bg-navy-800 py-16 sm:py-20 text-white">
          <div className="pointer-events-none absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-[#F26522] opacity-10 blur-3xl" />
          <div className="container-eos relative max-w-6xl reveal">
            <div className="max-w-3xl">
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight text-white">
                ¿Quieres saber si EOS <span className="text-[#F26522]">hace sentido para tu empresa?</span>
              </h2>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed">
                La mejor forma de entender EOS no es leyendo sobre el sistema, sino viéndolo aplicado a los desafíos reales de tu empresa.
              </p>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Te invito a una Reunión de 90 Minutos sin costo, junto a tu equipo de liderazgo. En ella conoceremos sus principales desafíos, les mostraré las herramientas de EOS y les explicaré exactamente cómo funciona el proceso de implementación.
              </p>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Al terminar tendrán toda la información necesaria para decidir si EOS es adecuado para ustedes.
              </p>
              <Link to="/contacto" data-testid="faq-final-cta-button" className="btn-primary mt-8">
                Agenda una Reunión de 90 Minutos conmigo
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </>
  );
}
